import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faChevronRight,
  faFistRaised,
  faCheck,
  faTimes,
  faClock,
  faCrosshairs,
  faExchangeAlt,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

/**
 * GlobalActiveLineup - Recreates the mockup's "Active Lineup" UI design
 * using the app's theme colors and real submission data.
 */
const GlobalActiveLineup = ({ leagueId }) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { data: dbUser, isLoading: isUserLoading } = useFetchUser();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubmissionIndex, setActiveSubmissionIndex] = useState(0);

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!dbUser?._id || !leagueId) return;

      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/lineup/active/${leagueId}/${dbUser._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setSubmissions(data);
        } else {
          setSubmissions([]);
        }
      } catch (error) {
        console.error("Error fetching global lineup:", error);
        setSubmissions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmissions();
  }, [dbUser?._id, leagueId, getAccessTokenSilently]);

  if (isUserLoading || isLoading) {
    return <LineupSkeleton />;
  }

  if (submissions.length === 0) {
    return <EmptyLineup onNavigate={() => navigate("/events")} />;
  }

  const activeSubmission = submissions[activeSubmissionIndex];

  return (
    <div className="space-y-4">
      {/* Submission Selector (if multiple) */}
      {submissions.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {submissions.map((sub, idx) => (
            <button
              key={sub._id}
              onClick={() => setActiveSubmissionIndex(idx)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                idx === activeSubmissionIndex
                  ? "bg-deep-red text-white shadow-md"
                  : "bg-gray-100 text-medium-grey hover:bg-gray-200"
              }`}
            >
              {sub.eventName || `Event ${idx + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Lineup Summary Card */}
      <LineupSummary submission={activeSubmission} />

      {/* Fighter Cards */}
      <div className="space-y-2">
        {activeSubmission.selections?.map((selection, idx) => (
          <LineupCard
            key={selection._id || idx}
            selection={selection}
            position={idx + 1}
            submission={activeSubmission}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() =>
            navigate(`/events/${activeSubmission.eventId}`, {
              state: { leagueId },
            })
          }
          className="flex-1 bg-deep-red hover:bg-bright-red text-white py-3 px-4 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon={faExchangeAlt} className="w-4 h-4" />
          Edit Lineup
        </button>
        <button
          onClick={() =>
            navigate(`/events/${activeSubmission.eventId}`, {
              state: { leagueId },
            })
          }
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-dark-grey py-3 px-4 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon={faFistRaised} className="w-4 h-4" />
          View Event
        </button>
      </div>
    </div>
  );
};

/**
 * Summary card showing total points, projected, and locked count
 */
const LineupSummary = ({ submission }) => {
  const selections = submission?.selections || [];
  const totalScore = submission?.total || 0;
  const projectedTotal = submission?.projectedTotal || 0;

  const eventDateTime = submission?.eventDateTime || submission?.DateTime;
  const hasEventStarted = eventDateTime
    ? new Date(eventDateTime) <= new Date()
    : false;

  // Count fighters by status
  const lockedCount = selections.filter((s) => {
    const outcome = s.lastUpdate?.outcome || "Pending";
    return outcome !== "Pending";
  }).length;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-lg text-dark-grey">Your Lineup</h2>
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-medium-grey">
          {lockedCount}/{selections.length} Scored
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-light-grey rounded-lg p-3">
          <div className="flex items-center gap-2 text-medium-grey mb-1">
            <FontAwesomeIcon icon={faCrosshairs} className="w-3.5 h-3.5" />
            <span className="text-xs">Total Points</span>
          </div>
          <p className="text-2xl font-bold text-dark-grey">
            {hasEventStarted || totalScore !== 0 ? totalScore.toFixed(2) : "—"}
          </p>
          <p className="text-xs text-medium-grey">
            Projected: {projectedTotal.toFixed(2)}
          </p>
        </div>
        <div className="bg-light-grey rounded-lg p-3">
          <div className="flex items-center gap-2 text-medium-grey mb-1">
            <FontAwesomeIcon icon={faFistRaised} className="w-3.5 h-3.5" />
            <span className="text-xs">Fighters</span>
          </div>
          <p className="text-2xl font-bold text-dark-grey">
            {selections.length}
          </p>
          <p className="text-xs text-medium-grey">
            {lockedCount > 0 ? `${lockedCount} completed` : "All pending"}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-deep-red rounded-full transition-all duration-500"
            style={{
              width: `${selections.length > 0 ? (lockedCount / selections.length) * 100 : 0}%`,
            }}
          />
        </div>
        <p className="text-xs text-medium-grey mt-1 text-right">
          {selections.length - lockedCount} fights remaining
        </p>
      </div>
    </div>
  );
};

/**
 * Individual fighter card matching the mockup design
 */
const LineupCard = ({ selection, position, submission }) => {
  const lastUpdate = selection.lastUpdate || {};
  const finalScore = lastUpdate.finalScore || 0;
  const actualOutcome = lastUpdate.outcome || "Pending";
  const isLotn = selection.lotn || lastUpdate.isLotn || false;

  const eventDateTime = submission?.eventDateTime || submission?.DateTime;
  const hasEventStarted = eventDateTime
    ? new Date(eventDateTime) <= new Date()
    : false;

  // Determine status
  const getStatus = () => {
    if (actualOutcome === "Pending" && hasEventStarted) return "live";
    if (actualOutcome === "Pending") return "pending";
    if (actualOutcome.toLowerCase().includes("win")) return "won";
    if (actualOutcome.toLowerCase().includes("loss")) return "lost";
    return "pending";
  };

  const status = getStatus();

  const getStatusIcon = () => {
    switch (status) {
      case "won":
        return (
          <FontAwesomeIcon
            icon={faCheck}
            className="w-3.5 h-3.5 text-green-500"
          />
        );
      case "lost":
        return (
          <FontAwesomeIcon
            icon={faTimes}
            className="w-3.5 h-3.5 text-red-500"
          />
        );
      case "live":
        return (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-deep-red"></span>
          </span>
        );
      default:
        return (
          <FontAwesomeIcon
            icon={faClock}
            className="w-3.5 h-3.5 text-medium-grey"
          />
        );
    }
  };

  const getCardBorder = () => {
    switch (status) {
      case "won":
        return "border-green-200 bg-green-50/50";
      case "lost":
        return "border-red-200 bg-red-50/30";
      case "live":
        return "border-deep-red/30 bg-deep-red/5";
      default:
        return "border-gray-100";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "won":
        return "text-green-600";
      case "lost":
        return "text-red-600";
      case "live":
        return "text-deep-red";
      default:
        return "text-medium-grey";
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border p-4 transition-all ${getCardBorder()}`}
    >
      <div className="flex items-start gap-3">
        {/* Position & Avatar */}
        <div className="flex flex-col items-center gap-1">
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-gray-400" />
          </div>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-gray-200 text-[10px] font-bold text-medium-grey">
            F{position}
          </span>
        </div>

        {/* Fighter Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-sm text-dark-grey truncate">
              {selection.fighter}
            </h3>
            {isLotn && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gold/20 text-gold border-0">
                <FontAwesomeIcon icon={faBolt} className="w-2 h-2" />
                LOTN
              </span>
            )}
          </div>
          <p className="text-xs text-medium-grey">
            {selection.opponent
              ? `vs ${selection.opponent}`
              : `Pick #${position}`}
          </p>
          <p className="text-xs text-medium-grey">
            Proj: {(selection.score || 0).toFixed(2)} pts
          </p>
        </div>

        {/* Status & Points */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1.5">
            {getStatusIcon()}
            <span
              className={`text-xs font-medium capitalize ${getStatusColor()}`}
            >
              {status}
            </span>
          </div>
          <p className="text-lg font-bold text-dark-grey">
            {status === "pending"
              ? "—"
              : finalScore >= 0
                ? `+${finalScore.toFixed(2)}`
                : finalScore.toFixed(2)}
          </p>
          <p className="text-[10px] text-medium-grey">
            {status === "pending"
              ? `Proj: ${(selection.score || 0).toFixed(2)}`
              : actualOutcome}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton matching the lineup card layout
 */
const LineupSkeleton = () => (
  <div className="space-y-4">
    {/* Summary skeleton */}
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-5 bg-gray-200 rounded w-28"></div>
        <div className="h-5 bg-gray-100 rounded-full w-20"></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-7 bg-gray-200 rounded w-16 mb-1"></div>
          <div className="h-3 bg-gray-100 rounded w-24"></div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
          <div className="h-7 bg-gray-200 rounded w-8 mb-1"></div>
          <div className="h-3 bg-gray-100 rounded w-20"></div>
        </div>
      </div>
      <div className="mt-3">
        <div className="h-2 bg-gray-100 rounded-full"></div>
      </div>
    </div>

    {/* Fighter card skeletons */}
    {Array.from({ length: 5 }).map((_, idx) => (
      <div
        key={idx}
        className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse"
      >
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center gap-1">
            <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            <div className="w-6 h-4 bg-gray-100 rounded"></div>
          </div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-100 rounded w-24 mb-1"></div>
            <div className="h-3 bg-gray-100 rounded w-20"></div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="h-3 bg-gray-200 rounded w-12"></div>
            <div className="h-6 bg-gray-200 rounded w-10"></div>
            <div className="h-3 bg-gray-100 rounded w-14"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Empty state when user has no global lineup
 */
const EmptyLineup = ({ onNavigate }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <FontAwesomeIcon
          icon={faFistRaised}
          className="text-gray-400 w-7 h-7"
        />
      </div>
      <h3 className="text-lg font-bold text-dark-grey mb-2">
        No Active Lineup
      </h3>
      <p className="text-medium-grey text-sm mb-6 max-w-[250px] mx-auto">
        You haven't submitted a lineup for the Global Competition yet. Pick an
        event to get started.
      </p>
      <button
        onClick={onNavigate}
        className="bg-deep-red hover:bg-bright-red text-white font-bold py-3 px-6 rounded-xl transition-colors text-sm inline-flex items-center gap-2"
      >
        Browse Events
        <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
      </button>
    </div>
  </div>
);

export default GlobalActiveLineup;
